import {StyleSheet, View, StyleProp, ViewStyle} from 'react-native';
import React, {ReactNode, useCallback, useEffect, useState} from 'react';
import PayWallChoosePlan, {
  IItemPlan,
  PayWallChoosePlanProps,
} from 'design-system/src/Widget/PayWall/PayWallChoosePlan/PayWallChoosePlan';
import {useTranslation} from 'gatsby-plugin-react-i18next';
import Metrics from 'design-system/src/Theme/Metrics';
import {useInAppPurchase} from '~Hooks/useInAppPurchase';
import {CustomActivityIndicator} from 'design-system/src/Components/CustomActivityIndicator';
import Typography from 'design-system/src/Components/Typography/Typography';
import useAppNavigation from '@src/utils/useAppNavigation';
import {Routes} from '@src/Constant/routes';
import queryString from 'query-string';
import useMember from '@src/Store/selectors/useMember';
import {usePromoCode} from '@src/Navigation/PromoCodePage/usePromocode';
import {IPaywallOrientation} from '../PaywallSection.type';
import {PaywallSectionTrackingProps} from '@src/Services/Amplitude/hooks/tracker';
import {PaywallSourceType} from '../PaywallSectionPage';
import {trackEvent, flushTracking} from '@src/Tracking/trackEvent';
import {TraceTokEvents} from '@src/Tracking/catalog';
interface IPaywallConfirmPageProps {
  variant?: PayWallChoosePlanProps['variant'];
  listFilter?: (item: IItemPlan) => boolean;
  productIdOrder?: string[];
  onMore?: boolean;
  children?: (props: {
    allSubscription: IItemPlan[];
    loading: boolean;
  }) => ReactNode;
  needStore?: boolean;
  horizontal?: boolean;
  contentWrapperStyle?: StyleProp<ViewStyle>;
  orientation?: IPaywallOrientation;
}

const PaywallConfirmPage = (props: IPaywallConfirmPageProps) => {
  const {
    variant,
    listFilter,
    children,
    onMore,
    needStore,
    horizontal,
    contentWrapperStyle,
    productIdOrder,
    orientation,
  } = props;
  const {
    handleBuySubscription,
    buyLoading,
    loading: loadingSub,
    allSubscription,
  } = useInAppPurchase(needStore);
  const {handleGetPromocode, loadingPromocode} = usePromoCode();
  const [selectedPlan, setSelectedPlan] = useState<IItemPlan>();
  const {t} = useTranslation('subscription');
  const navigation = useAppNavigation();
  const member = useMember();

  const loading: boolean = loadingPromocode || loadingSub;

  useEffect(() => {
    handleGetPromocode();
  }, []);

  const handleConfirm = useCallback(async () => {
    const subLink =
      selectedPlan?.subscriptionLink && member
        ? queryString.stringifyUrl({
            url: selectedPlan?.subscriptionLink,
            query: {
              client_reference_id: member?._id,
              prefilled_email: member?.email,
            },
          })
        : '';
    if (subLink) {
      // P1: client emits "intent" events; backend emits the subscription_started
      // truth event from the Stripe webhook so we don't double-count.
      // These MUST reach Amplitude before we navigate to Stripe, otherwise the
      // page unloads before the async send completes and the events are lost.
      try {
        await Promise.allSettled([
          trackEvent(TraceTokEvents.GoToSubscription, {
            paywall_context: variant,
            selected_offer_type: selectedPlan?.planId,
          }),
          trackEvent(TraceTokEvents.SubscriptionCheckoutStarted, {
            billing_provider: 'stripe',
            offer_type: selectedPlan?.planId,
            billing_period: selectedPlan?.subscriptionDuration,
            currency: selectedPlan?.currency,
          }),
        ]);
        await Promise.race([
          flushTracking(),
          new Promise(resolve => setTimeout(resolve, 600)),
        ]);
      } catch {
        // never block checkout on analytics
      }
      window.open(subLink, '_self');
    }
  }, [selectedPlan, member, variant]);

  const handleOnItemClick = useCallback((plan: IItemPlan) => {
    setSelectedPlan(plan);
  }, []);

  if (children) {
    return <>{children({allSubscription, loading})}</>;
  }

  const items = (
    listFilter ? allSubscription.filter(listFilter) : allSubscription
  )
    .map(item => ({
      ...item,
      price: item.price ? item.price : t('free'),
    }))
    .sort((a, b) => {
      const indexA = productIdOrder?.indexOf(a.planId) ?? -1;
      const indexB = productIdOrder?.indexOf(b.planId) ?? -1;
      return indexA - indexB;
    });
  return (
    <View style={{flex: 1}}>
      <View style={{paddingTop: Metrics.verticalScale(31)}}>
        {loading ? (
          <View style={styles.center}>
            <CustomActivityIndicator />
          </View>
        ) : (
          <View style={[styles.container, {}]}>
            {allSubscription.length > 0 ? (
              <PayWallChoosePlan
                orientation={orientation}
                loading={buyLoading}
                contentWrapperStyle={contentWrapperStyle}
                horizontal={horizontal}
                items={items}
                title={t('Choose your plan')}
                freeButtonTitle={t('By default')}
                primaryButtonTitle={t('Confirmed')}
                secondaryButtonTitle={t('Cancel')}
                thirdButtonTitle={t('See details')}
                onCancel={() =>
                  navigation.navigate(Routes.PROFILE_SUBSCRIPTION, {
                    replace: true,
                    state: {
                      headerTitle: '',
                      params: {
                        id: 'default',
                      },
                    },
                  })
                }
                onMore={
                  onMore
                    ? () => {
                        const trackingData: PaywallSectionTrackingProps = {
                          source_type: PaywallSourceType.Cta,
                          source_name: 'choose_plan_more',
                          source_url: location.href,
                          content_id: 'default',
                        };

                        navigation.navigate(Routes.PAYWALL, {
                          state: {
                            headerTitle: '',
                            params: {
                              id: 'default',
                              trackingData,
                            },
                          },
                        });
                      }
                    : undefined
                }
                variant={variant}
                onItemPress={handleOnItemClick}
                onConfirm={handleConfirm}
              />
            ) : (
              <View style={styles.center}>
                <Typography
                  children={t('No avalaible subscription')}
                  variant="h3"
                />
              </View>
            )}
          </View>
        )}
      </View>
      {/* {buyLoading && (
        <View style={styles.showLoading}>
          <CustomActivityIndicator />
        </View>
      )} */}
    </View>
  );
};

export default PaywallConfirmPage;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  showLoading: {
    flex: 1,
    width: '100%',
    height: '100%',
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
  center: {flex: 1, justifyContent: 'center', alignItems: 'center'},
});
